import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

import HeadSub from '../../../../components/comm/HeadSub';
import FoodRecognitionFailSheet from '../../../../components/home/food-record/guide-sheet/FoodRecognitionFailSheet';

import { loadMeal, saveMeal } from '../../../../utils/mealStorage';
import { mapPredictResult } from '../../../../utils/foodLensMapper';
import { putImage, newImageKey } from '../../../../utils/imageStore';
import { classifyFoodLensError } from '../../../../utils/foodLensError';
import { uploadDoinglabResult } from '../../../../api/dietAPI';
import { useBridge } from '../../../../bridge/BridgeProvider';
import URL from '../../../../constants/url';

import aiAnalyzingIcon from '../../../../images/common/i_ai_analyzing.png';
import style from './PhotoCapture.module.css';

// predict 무응답 방지. 실측 1.5초 내외지만 네트워크가 느리면 더 걸린다.
const PREDICT_TIMEOUT_MS = 30000;

const PhotoCapture = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { post, types, lastMessage, isNativeApp } = useBridge();

    const mealType = searchParams.get('meal') || 'lunch';
    const dateKey = searchParams.get('date') || dayjs().format('YYYY-MM-DD');

    const [engineReady, setEngineReady] = useState(false);
    const [analyzingDataUri, setAnalyzingDataUri] = useState(null);
    const [recogFailOpen, setRecogFailOpen] = useState(false);
    // 실패 원인별 안내 문구. 없으면 시트가 기본 "음식 인식 실패" 문구를 쓴다.
    const [failInfo, setFailInfo] = useState(null);

    // predict 에 넘긴 이미지 메타 (dataUri / width / height). 결과 매핑 기준이 된다.
    const pendingImageRef = useRef(null);
    const predictTimeoutRef = useRef(null);
    const lastHandledRef = useRef(null);
    const cameraOpenedRef = useRef(false);
    // 촬영/분석 진행 중 여부. 화면에 그리지 않고 브릿지 핸들러에서만 보므로 state 가 아닌 ref 로 둔다
    // (state 로 두면 effect 의존성 때문에 오래된 값을 읽게 된다).
    const busyRef = useRef(false);
    const setBusy = useCallback((v) => {
        busyRef.current = v;
    }, []);

    const clearPredictTimeout = () => {
        if (predictTimeoutRef.current) {
            clearTimeout(predictTimeoutRef.current);
            predictTimeoutRef.current = null;
        }
    };

    /** 실패 코드로 안내 문구를 정해 시트를 띄운다. code 는 브릿지 payload.code 또는 웹 자체 판정값. */
    const openFail = useCallback((code) => {
        const info = classifyFoodLensError(code);
        console.warn(`[PhotoCapture] fail code=${info.code} kind=${info.kind}`);
        setFailInfo(info);
        setRecogFailOpen(true);
    }, []);

    const triggerCamera = useCallback(() => {
        if (busyRef.current) return;
        setBusy(true);
        post(types.CUSTOM_CAMERA_OPEN);
    }, [post, types, setBusy]);

    const triggerGallery = useCallback(() => {
        if (busyRef.current) return;
        setBusy(true);
        post(types.IMAGE_PICK_GALLERY);
    }, [post, types, setBusy]);

    // 마운트 시 엔진 설정부터. 브라우저(네이티브 미연결)면 안내만 남기고 아무것도 안 한다.
    useEffect(() => {
        if (!isNativeApp) {
            console.warn('[PhotoCapture] 네이티브 미연결 — 촬영 기능은 앱에서만 동작합니다.');
            return;
        }
        post(types.FOODLENS_SET_ENGINE, { type: 'CALOAI' });
    }, [isNativeApp, post, types]);

    // 엔진 준비되면 카메라를 바로 연다 (촬영 가이드는 카메라 화면 안 ⓘ 버튼)
    useEffect(() => {
        if (!engineReady || cameraOpenedRef.current) return;
        cameraOpenedRef.current = true;
        triggerCamera();
    }, [engineReady, triggerCamera]);

    /**
     * predict 성공 → 로컬 저장(화면용) + 서버 전송.
     *
     * 서버 저장이 실패해도 로컬에는 남기고 화면은 진행시킨다.
     * 여기서 되돌리면 사용자는 방금 찍은 식사를 통째로 잃는다.
     */
    const handlePredictSuccess = useCallback(
        async (parsed, meta) => {
            const mapped = mapPredictResult(parsed, meta?.dataUri, meta?.width, meta?.height);

            // 기존 사진/음식에 덧붙인다. id 충돌을 피하려고 max+1 부터 다시 매긴다.
            const existing = loadMeal(dateKey, mealType);
            const baseId = existing.items.length
                ? Math.max(...existing.items.map((it) => Number(it.id) || 0))
                : 0;
            const reIded = mapped.items.map((it, idx) => ({ ...it, id: baseId + idx + 1 }));

            // dataUri 는 용량이 커서 localStorage 한도를 넘긴다 -> IndexedDB 에 두고 key 만 저장
            const imageRefs = await Promise.all(
                mapped.images.map(async (img) => {
                    const key = newImageKey();
                    try {
                        await putImage(key, img.imageUrl);
                    } catch (e) {
                        console.warn('[PhotoCapture] 이미지 저장 실패', e?.message);
                    }
                    return { key, labels: img.labels };
                }),
            );

            saveMeal(dateKey, mealType, {
                images: [...existing.images, ...imageRefs],
                items: [...existing.items, ...reIded],
            });

            try {
                await uploadDoinglabResult({
                    photoBase64: meta?.dataUri,
                    result: parsed,
                    recordDate: dateKey,
                    mealType,
                    srcWidth: meta?.width,
                    srcHeight: meta?.height,
                });
            } catch (e) {
                // 서버 저장 실패 — 로컬에는 남아 있으므로 화면은 그대로 진행한다.
                // 응답 본문이 객체로 와서 그냥 찍으면 [object Object] 가 되므로 직렬화해서 남긴다.
                const status = e?.response?.status ?? '(응답없음)';
                let body = e?.message;
                try {
                    if (e?.response?.data !== undefined) body = JSON.stringify(e.response.data);
                } catch {
                    body = String(e?.response?.data);
                }
                console.error(`[PhotoCapture] 서버 전송 실패 status=${status} body=${body}`);
            }

            // navigate(-1);
            navigate(URL.FOOD_RECORD_DETAIL, {state: {ntktmTyCd : mealType, recDe : dateKey}});
        },
        [dateKey, mealType, navigate],
    );

    // 브릿지 메시지 라우팅
    useEffect(() => {
        if (!lastMessage || lastHandledRef.current === lastMessage) return;
        lastHandledRef.current = lastMessage;

        const { type, payload } = lastMessage;

        if (type === types.FOODLENS_RESULT) {
            if (payload?.kind === 'setEngine') {
                post(types.FOODLENS_SET_LANGUAGE, { lang: 'KO' });
                return;
            }
            if (payload?.kind === 'setLanguage') {
                setEngineReady(true);
                return;
            }
            if (payload?.kind === 'predict') {
                if (!pendingImageRef.current) return;
                clearPredictTimeout();

                // SDK 결과는 문자열(toJSONString)로 오고 브릿지를 그대로 통과한다.
                // 봉투는 이미 파싱됐으므로 여기서 한 번 더 파싱해야 foods 가 잡힌다.
                let parsed = null;
                try {
                    parsed = typeof payload.data === 'string' ? JSON.parse(payload.data) : payload.data;
                } catch {
                    parsed = null;
                }

                const meta = pendingImageRef.current;
                pendingImageRef.current = null;
                setBusy(false);

                if (!parsed || !Array.isArray(parsed.foods) || parsed.foods.length === 0) {
                    openFail('EMPTY_RESULT');
                    return;
                }
                handlePredictSuccess(parsed, meta);
            }
            return;
        }

        if (type === types.FOODLENS_ERROR) {
            // 엔진/언어 설정 단계 실패 (토큰·앱등록 문제 등). 여기서 막히면 카메라가 아예
            // 안 열리므로 "카메라 준비 중..." 상태로 방치하지 말고 즉시 안내한다.
            if (payload?.kind === 'setEngine' || payload?.kind === 'setLanguage') {
                setBusy(false);
                openFail(payload?.code);
                return;
            }
            if (!pendingImageRef.current) return;
            clearPredictTimeout();
            pendingImageRef.current = null;
            setBusy(false);
            // 네이티브가 SDK 에러 종류를 code 로 넘겨준다 (FoodLensModule.kt errorCode())
            openFail(payload?.code);
            return;
        }

        if (type === types.IMAGE_PICK_RESULT) {
            if (!busyRef.current) return;
            if (payload?.cancelled) {
                setBusy(false);
                if (payload.reason === 'manual_input') {
                    navigate(URL.FOOD_MANUAL_ADD, { replace: true, state: { saveGbn: "I", ntktmTyCd: mealType, recDe: dateKey } });
                    return;
                }
                navigate(-1);
                return;
            }
            if (payload?.path) {
                pendingImageRef.current = {
                    dataUri: payload.dataUri ?? null,
                    width: payload.width ?? 0,
                    height: payload.height ?? 0,
                };
                setAnalyzingDataUri(payload.dataUri ?? null);
                post(types.FOODLENS_PREDICT, { imagePath: payload.path });

                clearPredictTimeout();
                predictTimeoutRef.current = setTimeout(() => {
                    if (!pendingImageRef.current) return;
                    pendingImageRef.current = null;
                    setBusy(false);
                    openFail('TIMEOUT');
                }, PREDICT_TIMEOUT_MS);
            }
            return;
        }

        if (type === types.IMAGE_PICK_ERROR) {
            if (!busyRef.current) return;
            clearPredictTimeout();
            pendingImageRef.current = null;
            setBusy(false);
            openFail(payload?.code);
        }
    }, [lastMessage, types, post, navigate, openFail, handlePredictSuccess, setBusy, mealType, dateKey]);

    useEffect(() => () => clearPredictTimeout(), []);

    // 시트에서 액션을 골라 닫힌 것인지, 그냥 닫은 것인지 구분
    const failActionTakenRef = useRef(false);

    const handleFailDismiss = () => {
        setRecogFailOpen(false);
        if (failActionTakenRef.current) {
            failActionTakenRef.current = false;
            return; // 액션을 골랐으면 이 화면에 머무른다
        }
        navigate(-1); // X / 배경 탭으로 닫으면 상세로 복귀
    };

    const handleRetake = () => {
        failActionTakenRef.current = true;
        setRecogFailOpen(false);
        setAnalyzingDataUri(null);
        triggerCamera();
    };

    const handlePickGallery = () => {
        failActionTakenRef.current = true;
        setRecogFailOpen(false);
        setAnalyzingDataUri(null);
        triggerGallery();
    };

    const goSearch = () => {
        failActionTakenRef.current = true;
        navigate(URL.FOOD_SEARCH, {state: {saveGbn : "I", ntktmTyCd : mealType, recDe : dateKey}});
    };

    const goManualAdd = () => {
        failActionTakenRef.current = true;
        // navigate(`${URL.FOOD_MANUAL_ADD}?meal=${mealType}&date=${dateKey}`);

        navigate(URL.FOOD_MANUAL_ADD, {state: {saveGbn : "I", ntktmTyCd : mealType, recDe : dateKey}})
    };

    return (
        <>
            <HeadSub title="사진 촬영" onBack={() => navigate(-1)} />

            <div className={style.cameraScreen}>
                {analyzingDataUri ? (
                    <>
                        <img
                            src={analyzingDataUri}
                            alt="분석 중인 사진"
                            className={style.capturedPhoto}
                        />
                        {!recogFailOpen && (
                            <div className={style.analyzingTextBox}>
                                <img src={aiAnalyzingIcon} alt="" className={style.analyzingIcon} />
                                <p>AI가 영양 정보를 분석 중입니다.</p>
                                <p>잠시만 기다려 주세요.</p>
                            </div>
                        )}
                    </>
                ) : (
                    <div className={style.placeholder}>
                        <p>{isNativeApp ? '카메라 준비 중...' : '촬영은 앱에서만 가능합니다.'}</p>
                    </div>
                )}
            </div>

            <FoodRecognitionFailSheet
                open={recogFailOpen}
                title={failInfo?.title}
                desc={failInfo?.desc}
                onDismiss={handleFailDismiss}
                onRetakePhoto={handleRetake}
                onScanBarcode={() => {}}
                onSelectGallery={handlePickGallery}
                onSearchFood={goSearch}
                onManualEntry={goManualAdd}
            />
        </>
    );
};

export default PhotoCapture;
